import { Modal } from '../../../components/modal/modal';
import { useState } from 'react';
import { Spinner } from '../../../components/spinner/spinner';
import { FileField } from '../../../components/field/field';
import FileSaver from 'file-saver';
import { DateTime } from 'luxon';
import { useImportExportApi } from '../../../clients/quipli-service/import-export/import-export-api';
import { Typography } from '../../../components/typography/typography';

export const ServiceImportExportModal = ({
  isOpen,
  isImport,
  setIsOpen,
  onFileUpload,
  onFileError,
}: {
  isOpen: boolean;
  isImport: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onFileUpload: () => void;
  onFileError: (message: string) => void;
}) => {
  const importExportAPI = useImportExportApi();

  // React states
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [file, setFile] = useState<File>();

  // Callbacks
  const exportServices = async () => {
    setRequestInProgress(true);
    const file = await importExportAPI.exportServices();

    const exportFileName = `Services_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const data = new Blob([file], { type: fileType });
    FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);

    setRequestInProgress(false);

    // Close modal
    setIsOpen(false);
    clear();
  };

  const importServices = async () => {
    if (!file) return;

    setRequestInProgress(true);

    if (file) {
      try {
        await importExportAPI.importServices(file);
        onFileUpload();
      } catch (error) {
        onFileError('Error importing services');
        setRequestInProgress(false);
        return;
      }
    }

    setRequestInProgress(false);

    // Close modal
    setIsOpen(false);
    clear();
  };

  const clear = () => {
    setFile(undefined);
  };

  const isButtonDisabled = (): boolean => {
    return isRequestInProgress || (isImport && !file);
  };

  const closeModal = () => {
    // Don't close modal if there is a request in progress
    if (isRequestInProgress) return;

    clear();
    setIsOpen(false);
  };

  const exportSampleFile = async () => {
    const file = await importExportAPI.exportServicesSample();

    const exportFileName = `Services_Sample_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const data = new Blob([file], { type: fileType });
    FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
  };

  return (
    <Modal
      isOpen={isOpen}
      title={isImport ? 'Import Services' : 'Export Services'}
      actions={
        isRequestInProgress ? (
          <Spinner />
        ) : (
          <>
            <button
              className="button button-small button-block button-secondary"
              disabled={isRequestInProgress}
              onClick={() => closeModal()}
            >
              Cancel
            </button>

            <button
              className="button button-small button-block button-primary"
              disabled={isButtonDisabled()}
              onClick={() => (isImport ? importServices() : exportServices())}
            >
              {isImport ? 'Import File' : 'Export'}
            </button>
          </>
        )
      }
      close={closeModal}
    >
      <div>
        {isImport && (
          <>
            <div className="text-center px-4 mb-4">
              <Typography type="text-sm" className="text-grey-500">
                <div>Import services from a XLS or XLSX spreadsheet file.</div>
                <div>
                  Click{' '}
                  <a className="text-quipli-black-800 underline cursor-pointer" onClick={exportSampleFile}>
                    here
                  </a>{' '}
                  to see the exact format of the worksheet.
                </div>
              </Typography>
            </div>
          </>
        )}

        {/* File */}
        {isImport && (
          <div className="mt-4">
            <FileField
              className="w-full h-full resize-none text-sm"
              label="File"
              value={file}
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(newFile) => setFile(newFile as File)}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
